import { UseSuspenseQueryOptions, useMutation, useQueryClient } from '@tanstack/react-query';

import { PublicationNewAliasRequest } from '@/module/Author/Publication/model/PublicationTypes';
import { TextFilters } from '@/module/Author/Text/model/TextTypes';

import { Id, PagedParams, PagedResponse, getQueryKey, invalidateAfterDelete } from '@feature/utils';

import * as api from './TranslationApi';
import { Translation, TranslationRequest } from './TranslationTypes';

const translationKey = getQueryKey('translation');

export function getTranslationPagedOptions(
  params: PagedParams<TextFilters>,
): UseSuspenseQueryOptions<PagedResponse<Translation>> {
  return {
    queryKey: translationKey.paged(params),
    queryFn: () => api.getPaged(params),
  };
}

export function getTranslationOptions(translationId: Id): UseSuspenseQueryOptions<Translation> {
  return {
    queryKey: translationKey.one(translationId),
    queryFn: () => api.get(translationId),
  };
}

export function useTranslationEdit(translationId: Id) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: TranslationRequest) => api.edit(translationId, request),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: translationKey.root(),
      }),
  });
}

export function useTranslationCreate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: TranslationRequest) => api.create(request),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: translationKey.root(),
      }),
  });
}

export function useTranslationDelete(translationId: Id) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.deleteById(translationId),
    onSuccess: () => invalidateAfterDelete(queryClient, translationKey, translationId),
  });
}

export function useTranslationAddNewAlias() {
  const queryClient = useQueryClient();
  return useMutation<Translation, Error, { translationId: Id; request: PublicationNewAliasRequest }>({
    mutationFn: ({ translationId, request }) => api.addNewAlias(translationId, request),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: translationKey.root(),
      }),
  });
}
